import { Fragment, useState } from 'react';
import { Button } from '@launchpad-ui/components';

import { Card, ErrorMessage, Field, PageHeader } from '../components/ui';
import {
  type NewHireInput,
  useCreateNewHire,
  useDeleteNewHire,
  useNewHires,
  useUpdateNewHire,
} from '../lib/hooks/useNewHires';
import { useReviewers } from '../lib/hooks/useReviewers';
import type { NewHire } from '../lib/types';

import styles from '../styles/common.module.css';

const EMPTY: NewHireInput = {
  name: '',
  email: '',
  review_start: '',
  review_end: '',
  buddy1: '',
  buddy2: '',
  region: '',
};

const REGIONS = ['EMEA', 'AMER', 'APAC'] as const;

export function NewHires() {
  const hiresQuery = useNewHires();
  const reviewersQuery = useReviewers();
  const create = useCreateNewHire();
  const update = useUpdateNewHire();
  const remove = useDeleteNewHire();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NewHireInput>(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<NewHireInput>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const hires = hiresQuery.data ?? [];
  const reviewerNames = (reviewersQuery.data ?? []).filter((r) => r.active).map((r) => r.name);
  const active = hires.filter((h) => h.active);
  const inactive = hires.filter((h) => !h.active);

  const submitNew = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync(form);
      setForm(EMPTY);
      setShowForm(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const startEdit = (h: NewHire) => {
    setEditingId(h.id);
    setEditForm({
      name: h.name,
      email: h.email ?? '',
      review_start: h.review_start ?? '',
      review_end: h.review_end ?? '',
      buddy1: h.buddy1 ?? '',
      buddy2: h.buddy2 ?? '',
      region: h.region ?? '',
    });
    setError(null);
  };

  const saveEdit = async () => {
    if (editingId == null) {return;}
    setError(null);
    try {
      await update.mutateAsync({ id: editingId, patch: editForm });
      setEditingId(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggle = (h: NewHire) => update.mutate({ id: h.id, patch: { active: h.active ? 0 : 1 } });

  const handleDelete = (id: number) => {
    if (window.confirm('Remove this new hire?')) {
      remove.mutate(id);
      if (editingId === id) {setEditingId(null);}
    }
  };

  return (
    <div className={styles.pageNarrow}>
      <PageHeader
        title="New Hires"
        actions={
          <Button
            onPress={() => {
              setShowForm((v) => !v);
              setError(null);
            }}
          >
            {showForm ? 'Cancel' : '+ Add New Hire'}
          </Button>
        }
      />

      {showForm && (
        <form onSubmit={submitNew}>
          <Card>
            <h2 className={styles.sectionTitle}>New Hire Details</h2>
            <ErrorMessage message={error} />
            <HireFormFields value={form} onChange={setForm} reviewerNames={reviewerNames} />
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" isDisabled={create.isPending}>
                {create.isPending ? 'Saving…' : 'Add New Hire'}
              </Button>
            </div>
          </Card>
        </form>
      )}

      {!showForm && <ErrorMessage message={error} />}

      <HireSection
        title="Active"
        hires={active}
        reviewerNames={reviewerNames}
        editingId={editingId}
        editForm={editForm}
        setEditForm={setEditForm}
        saving={update.isPending}
        onEdit={startEdit}
        onSaveEdit={saveEdit}
        onCancelEdit={() => setEditingId(null)}
        onToggle={toggle}
        onDelete={handleDelete}
      />
      {inactive.length > 0 && (
        <HireSection
          dimmed
          title="Inactive"
          hires={inactive}
          reviewerNames={reviewerNames}
          editingId={editingId}
          editForm={editForm}
          setEditForm={setEditForm}
          saving={update.isPending}
          onEdit={startEdit}
          onSaveEdit={saveEdit}
          onCancelEdit={() => setEditingId(null)}
          onToggle={toggle}
          onDelete={handleDelete}
        />
      )}

      {hiresQuery.isLoading && <div className={styles.empty}>Loading…</div>}
      {!hiresQuery.isLoading && hires.length === 0 && !showForm && (
        <div className={styles.empty}>No new hires yet. Add one to get started.</div>
      )}
    </div>
  );
}

function HireSection({
  title,
  hires,
  reviewerNames,
  editingId,
  editForm,
  setEditForm,
  saving,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onToggle,
  onDelete,
  dimmed,
}: {
  title: string;
  hires: NewHire[];
  reviewerNames: string[];
  editingId: number | null;
  editForm: NewHireInput;
  setEditForm: (v: NewHireInput) => void;
  saving: boolean;
  onEdit: (h: NewHire) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onToggle: (h: NewHire) => void;
  onDelete: (id: number) => void;
  dimmed?: boolean;
}) {
  if (!hires.length) {return null;}
  return (
    <div className={dimmed ? styles.dimmed : ''} style={{ marginBottom: '2rem' }}>
      <h2 className={styles.sectionTitle}>
        {title} ({hires.length})
      </h2>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>Review Window</th>
              <th className={styles.th}>Buddy 1</th>
              <th className={styles.th}>Buddy 2</th>
              <th className={styles.th}>Region</th>
              <th className={styles.th} />
            </tr>
          </thead>
          <tbody>
            {hires.map((h) => {
              const isEditing = editingId === h.id;
              return (
                <Fragment key={h.id}>
                  <tr>
                    <td className={styles.td} style={{ fontWeight: 600 }}>
                      {h.name}
                      {h.email && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--lp-color-text-ui-secondary)' }}>{h.email}</div>
                      )}
                    </td>
                    <td className={styles.td}>
                      {h.review_start && h.review_end
                        ? `${new Date(h.review_start).toLocaleDateString()} → ${new Date(h.review_end).toLocaleDateString()}`
                        : h.review_start
                          ? `From ${new Date(h.review_start).toLocaleDateString()}`
                          : '—'}
                    </td>
                    <td className={styles.td}>{h.buddy1 || '—'}</td>
                    <td className={styles.td}>{h.buddy2 || '—'}</td>
                    <td className={styles.td}>{h.region || '—'}</td>
                    <td className={styles.td}>
                      <div className={styles.actionsCell}>
                        <Button size="small" variant="minimal" onPress={() => (isEditing ? onCancelEdit() : onEdit(h))}>
                          {isEditing ? 'Cancel' : 'Edit'}
                        </Button>
                        <Button size="small" variant="minimal" onPress={() => onToggle(h)}>
                          {h.active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                        <Button size="small" variant="minimal" onPress={() => onDelete(h.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr>
                      <td className={styles.td} colSpan={6}>
                        <HireFormFields value={editForm} onChange={setEditForm} reviewerNames={reviewerNames} />
                        <div
                          style={{
                            marginTop: '0.75rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.5rem',
                          }}
                        >
                          <Button variant="minimal" onPress={onCancelEdit}>
                            Cancel
                          </Button>
                          <Button onPress={onSaveEdit} isDisabled={saving}>
                            {saving ? 'Saving…' : 'Save Changes'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HireFormFields({
  value,
  onChange,
  reviewerNames,
}: {
  value: NewHireInput;
  onChange: (v: NewHireInput) => void;
  reviewerNames: string[];
}) {
  const set = (patch: Partial<NewHireInput>) => onChange({ ...value, ...patch });
  return (
    <div className={styles.formGrid}>
      <Field label="Name" required>
        <input
          className={styles.input}
          type="text"
          required
          value={value.name}
          onChange={(e) => set({ name: e.target.value })}
        />
      </Field>
      <Field label="Email">
        <input
          className={styles.input}
          type="email"
          value={value.email ?? ''}
          onChange={(e) => set({ email: e.target.value })}
          placeholder="name@launchdarkly.com"
        />
      </Field>
      <Field label="Review Window Start">
        <input
          className={styles.input}
          type="date"
          value={value.review_start ?? ''}
          onChange={(e) => set({ review_start: e.target.value })}
        />
      </Field>
      <Field label="Review Window End">
        <input
          className={styles.input}
          type="date"
          value={value.review_end ?? ''}
          onChange={(e) => set({ review_end: e.target.value })}
        />
      </Field>
      <Field label="Buddy 1">
        <select className={styles.select} value={value.buddy1 ?? ''} onChange={(e) => set({ buddy1: e.target.value })}>
          <option value="">— Select reviewer —</option>
          {reviewerNames.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Buddy 2">
        <select className={styles.select} value={value.buddy2 ?? ''} onChange={(e) => set({ buddy2: e.target.value })}>
          <option value="">— Select reviewer —</option>
          {reviewerNames.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Region">
        <select className={styles.select} value={value.region ?? ''} onChange={(e) => set({ region: e.target.value })}>
          <option value="">— Select region —</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </Field>
    </div>
  );
}
